<template lang="pug">
.subunit-end.items-center.justify-center.relative.col-span-text(v-if="isSubunit && (screen.heading || screen.subheading || screen.text || screen.img)")
  p.mt-8.mb-6.flex.gap-6.justify-center.align-center
    uc-button.btn.btn-primary(@click="goToSection(parentDashboardUnitId, parentDashboardScreenId)")
      //- For translations
      //- text="Continue"
      uc-text(
        :text="buttonText || 'Continue'"
        type="span"
      )
.unit-end.flex.items-center.justify-center.relative.col-span-text.min-h-uc-screen(
  uc-dont-read=""
  uc-word-skip=""
  v-if="!screen.heading && !screen.subheading && !screen.text && !screen.img"
)
  //- .absolute.h-full.top-0.left-0.bg-warmgray-800.opacity-40.w-full
  //- .-z-1.fill.backdrop-blur-sm(:data-backdrop="screen.id" class="bg-gray-800/50")

  .z-10.flex.flex-col.gap-12.items-center.justify-center.w-full(
    :class="{ 'pt-32': isReturnToDashboard }"
    uc-fade=""
  )
    p.flex.flex-col.gap-12.items-center.justify-center.w-full()
      uc-text.heading.text-center.dark_text-white(
        :data-unit-number="screen.id"
        text="You have completed"
        type="span"
      )

      | #{ ' ' }

      span.unit-subtitle.text-center.dark_text-white.inline-block.mx-auto(
        :data-unit-title="screen.id"
      )
        //- Translations cannot handle custom unit labels!
        //- text="Unit ${unitNumber}"
        uc-text.unit-subtitle.unit-label.text-center.dark_text-white.inline-block.mx-auto(
          :text="'${unitLabel} ${unitNumber}'"
          :values="{ unitNumber: unit?.number || '' }"
          k="Unit ${unitNumber}"
          type="span"
          v-if="showUnitLabel"
        )
        span.unit-label-separator(v-if="showUnitLabel")
          | : #{ ' ' }

        uc-text.unit-subtitle.title.text-center.dark_text-white.inline-block.mx-auto(
          :k="`${pageId}.title`"
          :text="removeLinebreaks(pageTitle)"
          type="span"
          uc-word-bold=""
        )
    uc-preload-sfx(name="complete_unit")
    .text-center(v-if="next")
      router-link.scroll-down-button.unlock.inline-block.text-primary-700.dark_text-white.p-2.focus-visible_ring(
        :to="'/' + next.id"
        class="mt-[30vh]"
        data-sfx="complete_unit"
        v-if="!isReturnToDashboard && (isLinear || unlockCourse || next.number || (next.type === 'final' && finalUnlocked))"
      )
        //- .text-center.inline-block.text-2xl.animate-bounce.mb-4
        //-   span.iconify(data-icon="bytesize:chevron-bottom")   
        span.text-center.font-semibold.tracking-wider.title-scroll-text
          .text-lg
            span(v-if="next.number && showUnitLabel") 
              //- Translations cannot handle custom unit labels!
              //- text="Continue to Unit ${nextNumber}:"
              uc-text(
                :text="'Continue to ${unitLabel} ${nextNumber}:'"
                :values="{ nextNumber: unit.number + 1 }"
                k="Continue to Unit ${nextNumber}:"
              )

            span(v-else-if="next.type === 'final' && showUnitLabel") 
              //- Translations cannot handle custom unit labels!
              //- text="Continue to final unit:"
              uc-text(
                :text="'Continue to the final ${unitLabel}:'"
                :values="{ unitLabel: unitLabel.toLowerCase() }"
                k="Continue to final unit:"
              )

            | #{ ' ' }

            span(class="mx-[1ch]" v-if="showUnitLabel") #{ ' ' }

            | #{ ' ' }

            uc-text(:k="`${next.id}.title`" :text="removeLinebreaks(next.title)" uc-word-bold="")

            //- bouncing right arrow (flipped for rtl)
            span.text-2xl.ml-4.rtl_mr-4.rtl_ml-0.relative.inline-block.rtl_-scale-x-100
              uc-icon.animate-bounce-left(icon="theme:right")

      template(v-else="")
        .prose-stack.w-full.max-w-none
          p.italic.center.m-8.max-w-none
            uc-text(
              :text="content.course['unit-end-dashboard-instructions']"
              k="course.unit-end-dashboard-instructions"
              v-if="content.course['unit-end-dashboard-instructions']"
            )

            //- Translations cannot handle custom unit labels!
            //- text="Please choose the next unit to continue."
            uc-text(
              :text="`Please choose the next ${unitLabel.toLowerCase()} to continue.`"
              k="Please choose the next unit to continue."
              v-else-if="isLinear"
            )

            //- Translations cannot handle custom unit labels!
            //- text="Please choose any unit to continue."
            uc-text(
              :text="`Please choose any ${unitLabel.toLowerCase()} to continue.`"
              k="Please choose any unit to continue."
              v-else=""
            )

        screen-dashboard(:screen="{ ...dashboardProps, ...screen }" :edit-path="editPath")

    .text-center(v-if="isSubunit")
      p.mt-8.mb-6.flex.gap-6.justify-center.align-center
        uc-button.btn.btn-primary(@click="goToSection(parentDashboardUnitId, parentDashboardScreenId)")
          uc-text(
            :text="buttonText || 'Continue'"
            type="span"
          )
</template>

<script lang="ts">
import { inject, ref, watch, unref, toRef } from 'vue';
import type { Ref } from 'vue';
import useContent from '../stores/useContent';
import removeLinebreaks from '../lib/removeLinebreaks';
import useScreenAudio from '../lib/useScreenAudio';
import unitEndSchema from '../lib/screen-schemas/screen-unit-end.schema';

export default {
  inject: [
    'pageTitle',
    'nextUnits',
    'pageId',
    'unit',
    'isEditModeActive',
    'finalUnlocked',
    'unlockCourse'
  ],
  props: {
    editPath: { type: String, required: true },
    screen: { type: Object, required: true },
    isSubunit: { type: Boolean, required: false, default: false },
    nested: { type: Boolean, required: false, default: false }
  },
  setup(props) {
    const { compiled } = useContent();

    const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));
    const pageTitle = inject<string | Ref<string>>('pageTitle');
    const pageId = inject<string>('pageId');
    const { playSlot, queueSlot } = useScreenAudio(
      toRef(props, 'screen'),
      unitEndSchema
    );

    watch(sectionActive, active => {
      if (props.nested) return;
      if (!active) return;
      // Subunit case: YAML heading/text fields are present — schema derives the text.
      // Regular unit-end: text comes from the injected store pageTitle.
      if (
        props.screen.heading ||
        props.screen.subheading ||
        props.screen.text
      ) {
        playSlot('intro');
      } else {
        const id = unref(pageId);
        const title = unref(pageTitle);
        playSlot('intro', 'You have completed');
        queueSlot('intro', removeLinebreaks(title), `${id}.title`);
      }
    });

    return { content: compiled };
  },
  computed: {
    next() {
      return this.nextUnits[this.pageId] || null;
    },
    isReturnToDashboard() {
      return this.content.course['return-to-dashboard'] || null;
    },
    isLinear() {
      return this.content.course['linear'] || null;
    },
    showUnitLabel() {
      return this.content.course['hide-unit-labels'] ? false : true;
    },
    unitLabel() {
      // text="Unit"  <= for npm run translate
      return this.content.course['unit-label'] || 'Unit';
    },
    dashboardLocation() {
      for (const page of this.content.pages) {
        for (const screen of page.screens) {
          if (screen.template === 'dashboard') {
            return { page: page, screen: screen };
          }
        }
      }

      return {};
    },
    dashboardProps() {
      return {
        cards: this.dashboardLocation?.screen?.cards ?? true,
        banner: this.dashboardLocation?.screen?.banner ?? false,
        'dashboard-layout':
          this.dashboardLocation?.screen['dashboard-layout'] ?? false,
        hotspot: this.dashboardLocation?.screen?.hotspot ?? false,
        'hide-card-tint':
          this.dashboardLocation?.screen?.['hide-card-tint'] ?? false,
        styles: {
          'db-card': this.dashboardLocation?.screen?.styles?.['db-card'] ?? '',
          'db-banner':
            this.dashboardLocation?.screen?.styles?.['db-banner'] ?? '',
          'db-panel': this.dashboardLocation?.screen?.styles?.['db-panel'] ?? ''
        }
      };
    },
    parentDashboardScreenId() {
      return this.content.pages
        .map(page => page.screens)
        .flat()
        .filter(screen => screen.template === 'subunit-dashboard')
        .filter(screen => screen.subunits?.includes(this.pageId))[0]
        ?.id.toString();
    },
    parentDashboardUnitId() {
      return this.content.pages
        .filter(page =>
          page.screens
            .map(screen => screen.id)
            .includes(this.parentDashboardScreenId)
        )[0]
        ?.id.toString();
    },
    buttonText() {
      return typeof this.screen?.continue === 'string'
        ? this.screen?.continue
        : typeof this.screen?.start === 'string'
          ? this.screen?.start
          : null;
    }
  },
  methods: {
    removeLinebreaks,
    async goToSection(pageId, sectionId, { duration = 1 } = {}) {
      // if (isHidden(pageId) || isHidden(sectionId)) return;

      if (this.currentLocation !== pageId) {
        this.$router.push('/' + pageId);

        await new Promise(r => setTimeout(r, 500));
      }

      const scroller = document.querySelector(`#${pageId} .scroller`);

      if (!scroller) return;

      const section = document.querySelector(`#${sectionId}`);

      if (section) {
        section.scrollIntoView({
          behavior: 'instant',
          block: 'start',
          inline: 'nearest'
        });
      }
    }
  }
};
</script>
